import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  Stack,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { ReportPageView } from "./ReportPageView";
import { SlideFrame } from "./SlideFrame";
import { splitIntoPages } from "../utils/pages";
import { resolveBranding } from "../utils/branding";
import { useClientBranding } from "../../../../../shared/hooks";
import type { ReportBlock } from "../api/types";

interface PresentationModeProps {
  open: boolean;
  onClose: () => void;
  blocks: ReportBlock[];
  clientId: string | null;
  /** Presigned narration audio URL, if narration has been generated. */
  audioUrl?: string | null;
  /** One transcript segment per page, aligned with the pages below. */
  segments?: string[];
}

/**
 * PowerPoint-style fullscreen presentation. Steps through report pages; when
 * narration audio is present it plays the whole track and auto-advances pages
 * on a fixed cadence derived from segment count. Arrow keys / on-screen buttons
 * navigate; Escape closes.
 *
 * Audio note: narration is a single concatenated MP3 (one segment per page).
 * We can't seek to arbitrary segment offsets reliably across browsers without
 * per-segment timestamps, so playback runs start-to-finish and pages advance on
 * a time budget split evenly across pages. Manual navigation pauses auto-advance.
 */
export const PresentationMode: React.FC<PresentationModeProps> = ({
  open,
  onClose,
  blocks,
  clientId,
  audioUrl,
  segments,
}) => {
  const pages = useMemo(() => splitIntoPages(blocks), [blocks]);
  const { data: brandingData } = useClientBranding(clientId);
  const branding = useMemo(
    () => resolveBranding(brandingData?.data),
    [brandingData],
  );
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pageCount = pages.length;

  const goTo = (next: number) => {
    setIndex((prev) => {
      const clamped = Math.max(0, Math.min(pageCount - 1, next));
      return clamped;
    });
  };

  // Reset when opened.
  useEffect(() => {
    if (open) {
      setIndex(0);
      setPlaying(false);
      setAutoAdvance(false);
    }
  }, [open]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        setAutoAdvance(false);
        goTo(index + 1);
      } else if (e.key === "ArrowLeft") {
        setAutoAdvance(false);
        goTo(index - 1);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, pageCount, onClose]);

  // Auto-advance while narration plays: divide total audio duration evenly
  // across pages and step on each slice.
  useEffect(() => {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    if (!autoAdvance || !playing || pageCount <= 1) return;

    const audio = audioRef.current;
    const total = audio && isFinite(audio.duration) ? audio.duration : 0;
    if (!total) return;
    const perPage = (total / pageCount) * 1000;

    advanceTimer.current = setTimeout(() => {
      setIndex((prev) => (prev + 1 < pageCount ? prev + 1 : prev));
    }, perPage);

    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, [autoAdvance, playing, index, pageCount]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      setAutoAdvance(false);
    } else {
      setIndex(0);
      audio.currentTime = 0;
      void audio.play();
      setPlaying(true);
      setAutoAdvance(true);
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        {/* Top bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {index + 1} of {pageCount}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {audioUrl && (
              <Tooltip title={playing ? "Pause narration" : "Play narration"}>
                <IconButton onClick={togglePlay}>
                  {playing ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Exit presentation">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {pageCount > 0 && (
          <LinearProgress
            variant="determinate"
            value={((index + 1) / pageCount) * 100}
          />
        )}

        {/* Slide */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            p: { xs: 2, md: 6 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 1100, minHeight: 480 }}>
            {pages[index] && (
              <SlideFrame branding={branding}>
                <ReportPageView
                  blocks={pages[index]}
                  clientId={clientId}
                  branding={branding}
                  variant="screen"
                />
              </SlideFrame>
            )}
          </Box>
        </Box>

        {/* Nav */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}
        >
          <IconButton
            onClick={() => {
              setAutoAdvance(false);
              goTo(index - 1);
            }}
            disabled={index === 0}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          {segments?.[index] && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1, textAlign: "center", px: 2 }}
              noWrap
            >
              {segments[index]}
            </Typography>
          )}
          <IconButton
            onClick={() => {
              setAutoAdvance(false);
              goTo(index + 1);
            }}
            disabled={index >= pageCount - 1}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Stack>

        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => {
              setPlaying(false);
              setAutoAdvance(false);
            }}
            style={{ display: "none" }}
          />
        )}
      </Box>
    </Dialog>
  );
};
